import React, { useEffect, useMemo, useRef, useState } from 'react'
import axios, { AxiosError } from 'axios'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import ThreeDRotationIcon from '@mui/icons-material/ThreeDRotation'
import ImageUploader from './ImageUploader'

import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'

function useObjectUrl(blob: Blob | null): string | null {
  const url = useMemo(() => {
    if (!blob) return null
    return URL.createObjectURL(blob)
  }, [blob])

  useEffect(() => {
    return () => {
      if (url) URL.revokeObjectURL(url)
    }
  }, [url])

  return url
}

const GLBViewer: React.FC<{ url: string }> = ({ url }) => {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const width = container.clientWidth
    const height = container.clientHeight

    const scene = new THREE.Scene()
    scene.background = new THREE.Color(0xf7f7f9)

    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 2000)
    camera.position.set(0, 1.2, 2.2)

    const renderer = new THREE.WebGLRenderer({ antialias: true })
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))
    renderer.setSize(width, height)
    container.appendChild(renderer.domElement)

    const ambient = new THREE.AmbientLight(0xffffff, 0.9)
    scene.add(ambient)

    const keyLight = new THREE.DirectionalLight(0xffffff, 0.8)
    keyLight.position.set(5, 10, 5)
    scene.add(keyLight)

    const fillLight = new THREE.DirectionalLight(0xffffff, 0.4)
    fillLight.position.set(-5, 4, -4)
    scene.add(fillLight)

    const controls = new OrbitControls(camera, renderer.domElement)
    controls.enableDamping = true
    controls.dampingFactor = 0.08

    const loader = new GLTFLoader()
    let modelRoot: THREE.Object3D | null = null

    const disposeSceneObject = (obj: THREE.Object3D) => {
      obj.traverse((child: any) => {
        if (child.geometry) child.geometry.dispose?.()
        if (child.material) {
          if (Array.isArray(child.material)) child.material.forEach((m: any) => m.dispose?.())
          else child.material.dispose?.()
        }
      })
    }

    loader.load(
      url,
      (gltf) => {
        modelRoot = gltf.scene
        scene.add(modelRoot)

        const box = new THREE.Box3().setFromObject(modelRoot)
        const size = box.getSize(new THREE.Vector3())
        const center = box.getCenter(new THREE.Vector3())

        modelRoot.position.x += modelRoot.position.x - center.x
        modelRoot.position.y += modelRoot.position.y - center.y
        modelRoot.position.z += modelRoot.position.z - center.z

        const maxDim = Math.max(size.x, size.y, size.z)
        const distance = maxDim * 1.8
        camera.position.set(0, maxDim * 0.2, distance)
        controls.target.set(0, 0, 0)
        controls.update()
      },
      undefined,
      (error) => {
        // eslint-disable-next-line no-console
        console.error('GLB load error', error)
      }
    )

    let raf = 0
    const animate = () => {
      controls.update()
      renderer.render(scene, camera)
      raf = window.requestAnimationFrame(animate)
    }
    animate()

    const onResize = () => {
      const newWidth = container.clientWidth
      const newHeight = container.clientHeight
      camera.aspect = newWidth / newHeight
      camera.updateProjectionMatrix()
      renderer.setSize(newWidth, newHeight)
    }
    window.addEventListener('resize', onResize)

    return () => {
      window.removeEventListener('resize', onResize)
      window.cancelAnimationFrame(raf)
      controls.dispose()
      renderer.dispose()
      if (modelRoot) {
        disposeSceneObject(modelRoot)
        scene.remove(modelRoot)
      }
      container.removeChild(renderer.domElement)
    }
  }, [url])

  return <Box ref={containerRef} sx={{ width: '100%', height: 520, borderRadius: 2 }} />
}

async function tryDecodeApiError(error: unknown): Promise<string> {
  const fallback = (error as any)?.message || 'Błąd generowania modelu 3D'

  if (!axios.isAxiosError(error)) return fallback
  const axiosError = error as AxiosError

  const contentType = String(axiosError.response?.headers?.['content-type'] || '')
  const status = axiosError.response?.status

  if (!axiosError.response) return fallback

  if (contentType.includes('application/json')) {
    try {
      const text =
        axiosError.response.data instanceof ArrayBuffer
          ? new TextDecoder().decode(new Uint8Array(axiosError.response.data))
          : JSON.stringify(axiosError.response.data)
      const parsed = JSON.parse(text)
      return parsed?.error || parsed?.message || `HTTP ${status}`
    } catch {
      return `HTTP ${status}`
    }
  }

  return `HTTP ${status}`
}

const Avatar3DTool: React.FC = () => {
  const [imageData, setImageData] = useState<string>('')
  const [fileName, setFileName] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string>('')
  const [outputBlob, setOutputBlob] = useState<Blob | null>(null)
  const [engine, setEngine] = useState<'trellis2' | 'hunyuan3d'>('trellis2')
  const [outputFormat, setOutputFormat] = useState<'glb' | 'obj_zip'>('glb')
  const [resultFormat, setResultFormat] = useState<'glb' | 'obj_zip' | null>(null)
  const [successMessage, setSuccessMessage] = useState<string>('')
  const [resultProvider, setResultProvider] = useState<string>('')
  const [uploadNotice, setUploadNotice] = useState<string>('')

  const outputUrl = useObjectUrl(outputBlob)

  const maxUploadBytes = 1024 * 1024

  useEffect(() => {
    if (engine === 'trellis2' && outputFormat !== 'glb') {
      setOutputFormat('glb')
    }
  }, [engine, outputFormat])

  const loadImage = (dataUrl: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = () => reject(new Error('Nie mozna wczytac obrazu'))
      img.src = dataUrl
    })

  const blobToDataUrl = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = () => reject(new Error('Nie mozna odczytac obrazu'))
      reader.readAsDataURL(blob)
    })

  const downscaleToLimit = async (dataUrl: string, maxBytes: number) => {
    const img = await loadImage(dataUrl)
    let scale = 1
    let quality = 0.9
    let lastBlob: Blob | null = null

    for (let i = 0; i < 7; i += 1) {
      const width = Math.max(320, Math.round(img.width * scale))
      const height = Math.max(320, Math.round(img.height * scale))
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      if (!ctx) break
      ctx.drawImage(img, 0, 0, width, height)

      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/jpeg', quality)
      )
      if (!blob) break
      lastBlob = blob
      if (blob.size <= maxBytes) {
        return blob
      }
      scale *= 0.85
      quality = Math.max(0.6, quality * 0.85)
    }

    if (!lastBlob) {
      throw new Error('Nie udalo sie przetworzyc obrazu')
    }
    if (lastBlob.size > maxBytes) {
      throw new Error('Nie udalo sie zejsc ponizej limitu 1 MB')
    }
    return lastBlob
  }

  const handleUpload = async (dataUrl: string, file: File) => {
    setError('')
    setSuccessMessage('')
    setResultProvider('')
    setUploadNotice('')
    setOutputBlob(null)
    setResultFormat(null)

    let nextDataUrl = dataUrl
    let nextFileName = file.name

    if (file.size > maxUploadBytes) {
      const isJpeg = file.type === 'image/jpeg' || file.name.toLowerCase().endsWith('.jpg') || file.name.toLowerCase().endsWith('.jpeg')
      if (!isJpeg) {
        const confirmConvert = window.confirm(
          'Plik przekracza 1 MB. Czy zamienic na JPG i zmniejszyc rozmiar?'
        )
        if (!confirmConvert) {
          setError('Plik jest za duzy. Wybierz mniejszy obraz lub zgadz sie na konwersje do JPG.')
          return
        }
      }

      try {
        const resizedBlob = await downscaleToLimit(dataUrl, maxUploadBytes)
        nextDataUrl = await blobToDataUrl(resizedBlob)
        nextFileName = file.name.replace(/\.[^/.]+$/, '') + '.jpg'
        setUploadNotice('Obraz zostal zmniejszony do limitu 1 MB (JPG).')
      } catch (err: any) {
        setError(err?.message || 'Nie udalo sie zmniejszyc obrazu do 1 MB')
        return
      }
    }

    setImageData(nextDataUrl)
    setFileName(nextFileName)
  }

  const handleGenerate = async () => {
    if (!imageData) return
    setIsLoading(true)
    setError('')
    setSuccessMessage('')
    setResultProvider('')
    setOutputBlob(null)
    setResultFormat(null)

    try {
      const resp = await axios.post(
        '/api/photo-tools/avatar-3d',
        { image: imageData, engine, output_format: outputFormat },
        {
          responseType: 'arraybuffer',
          timeout: 10 * 60 * 1000
        }
      )

      const contentType = String(resp.headers?.['content-type'] || '')
      const receivedFormat = contentType.includes('application/zip') ? 'obj_zip' : 'glb'
      const blobType = receivedFormat === 'obj_zip' ? 'application/zip' : 'model/gltf-binary'
      const providerHeader = String(resp.headers?.['x-avatar3d-provider'] || '')
      const blob = new Blob([resp.data], { type: blobType })
      setOutputBlob(blob)
      setResultFormat(receivedFormat)
      setResultProvider(providerHeader)
      const formatLabel = receivedFormat === 'obj_zip' ? 'OBJ+MTL+PNG (ZIP)' : 'GLB'
      const providerLabel = providerHeader ? ` • ${providerHeader}` : ''
      setSuccessMessage(`Generacja zakonczona: ${formatLabel}${providerLabel}`)
    } catch (e: any) {
      setError(await tryDecodeApiError(e))
    } finally {
      setIsLoading(false)
    }
  }

  const handleDownload = () => {
    if (!outputBlob || !outputUrl) return

    const a = document.createElement('a')
    a.href = outputUrl
    a.download = resultFormat === 'obj_zip' ? 'avatar-obj.zip' : 'avatar.glb'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  return (
    <Stack spacing={3}>
      <Paper elevation={2} sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Typography variant="h6" fontWeight={600}>
            Wejście
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Wgraj zdjęcie osoby. Wybierz silnik 3D oraz format wyjścia i uruchom generowanie.
          </Typography>

          <ImageUploader label="Zdjęcie" onImageUpload={handleUpload} currentImage={imageData || undefined} />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              select
              label="Silnik 3D"
              value={engine}
              onChange={(event) => setEngine(event.target.value as 'trellis2' | 'hunyuan3d')}
              size="small"
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="trellis2">Trellis 2</MenuItem>
              <MenuItem value="hunyuan3d">Hunyuan 3D 3.0</MenuItem>
            </TextField>

            <TextField
              select
              label="Format wyjscia"
              value={outputFormat}
              onChange={(event) => setOutputFormat(event.target.value as 'glb' | 'obj_zip')}
              size="small"
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="glb">GLB</MenuItem>
              <MenuItem value="obj_zip" disabled={engine !== 'hunyuan3d'}>
                OBJ+MTL+PNG (ZIP)
              </MenuItem>
            </TextField>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <Button
              variant="contained"
              size="large"
              startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : <ThreeDRotationIcon />}
              onClick={handleGenerate}
              disabled={!imageData || isLoading}
              data-testid="avatar3d-generate-button"
            >
              Generuj 3D avatar
            </Button>

            <Typography variant="body2" color="text.secondary">
              {fileName ? `Plik: ${fileName}` : 'Brak pliku'}
            </Typography>
          </Stack>

          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}

          {uploadNotice && (
            <Alert severity="info">{uploadNotice}</Alert>
          )}
        </Stack>
      </Paper>

      <Paper elevation={2} sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Typography variant="h6" fontWeight={600}>
            Wynik
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Obracaj model myszką (drag). Scroll = zoom.
          </Typography>

          <Divider />

          {isLoading && (
            <Stack spacing={1}>
              <LinearProgress />
              <Typography variant="body2" color="text.secondary">
                Trwa generowanie modelu 3D. To moze potrwac okolo 1-2 minuty.
              </Typography>
            </Stack>
          )}

          {successMessage && !isLoading && (
            <Alert severity="success">{successMessage}</Alert>
          )}

          {outputUrl ? (
            <>
              {resultFormat === 'glb' ? (
                <GLBViewer url={outputUrl} />
              ) : (
                <Box
                  sx={{
                    height: 200,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'action.hover',
                    borderRadius: 2
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Wynik to paczka ZIP (OBJ+MTL+PNG). Podglad w przegladarce jest niedostepny.
                  </Typography>
                </Box>
              )}

              <Stack direction="row" spacing={2}>
                <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownload}>
                  Pobierz plik
                </Button>
              </Stack>
            </>
          ) : (
            <Box
              sx={{
                height: 200,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'action.hover',
                borderRadius: 2
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Brak modelu. Wgraj zdjęcie i uruchom generowanie.
              </Typography>
            </Box>
          )}
        </Stack>
      </Paper>
    </Stack>
  )
}

export default Avatar3DTool
